// Desktop (md+) persistent left navigation. On phones the same entries live in the bottom tab
// bar (app-shell.tsx); sign-out is in the avatar menu.

import { Link, NavLink } from "react-router-dom";
import { ArrowLeftRight, DoorOpen, LogOut, ScanQrCode } from "lucide-react";

import { useAuth } from "@/lib/auth";
import { useMe } from "@/lib/me";
import { APP_NAME, visibleSections } from "@/lib/nav";
import { useSessionScope } from "@/lib/session-scope";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

export function AppSidebar() {
  const { signOut } = useAuth();
  const { me } = useMe();
  const { scope } = useSessionScope();
  // Switching only makes sense with a choice (or for an admin who reached /admin without a gate).
  const canSwitch = (me?.gates.length ?? 0) > 1 || (!scope && (me?.gates.length ?? 0) > 0);

  function handleSignOut() {
    // signOut() also ends the org-wide ICM session and navigates away.
    signOut();
  }

  const entries = visibleSections(!!me?.isAdmin).map((s) => ({ ...s, end: s.href === "/" }));

  return (
    <aside className="hidden w-60 shrink-0 flex-col border-r border-border bg-card md:flex">
      <Link to="/" className="flex h-14 items-center border-b border-border px-5 text-base font-semibold">
        {APP_NAME}
      </Link>

      {/* The gate this session works at. */}
      <div className="px-3 pt-3">
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
          <div className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">Current gate</div>
          {scope ? (
            <>
              <div className="mt-0.5 flex items-center gap-2">
                <DoorOpen className="size-4 shrink-0 text-primary" />
                <span className="min-w-0 truncate font-semibold" title={scope.gate.name}>
                  {scope.gate.name}
                </span>
              </div>
              {(scope.gate.operationUnit?.name || scope.gate.description) && (
                <div className="mt-0.5 truncate text-xs text-muted-foreground">
                  {[scope.gate.operationUnit?.name, scope.gate.description].filter(Boolean).join(" · ")}
                </div>
              )}
            </>
          ) : (
            <div className="mt-0.5 text-sm text-muted-foreground">None selected</div>
          )}
          {canSwitch && (
            <Button
              variant="outline"
              size="sm"
              nativeButton={false}
              className="mt-2 w-full bg-card"
              render={<Link to="/select-gate?switch=1" />}
            >
              <ArrowLeftRight /> {scope ? "Switch gate" : "Choose gate"}
            </Button>
          )}
        </div>
      </div>

      <div className="p-3">
        <Button nativeButton={false} className="w-full" render={<Link to="/scan" />}>
          <ScanQrCode /> Scan a gate pass
        </Button>
      </div>

      <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3" aria-label="Primary">
        {entries.map((i) => (
          <NavLink
            key={i.href}
            to={i.href}
            end={i.end}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                isActive ? "bg-accent font-medium text-accent-foreground" : "hover:bg-accent"
              )
            }
          >
            <i.icon className="size-4 text-muted-foreground" />
            {i.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-3">
        <Separator className="mb-3" />
        <Button
          variant="ghost"
          onClick={handleSignOut}
          className="w-full justify-start gap-3 px-3 text-sm font-normal text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <LogOut className="size-4" /> Sign out
        </Button>
      </div>
    </aside>
  );
}
